import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FaSearch, FaTimes, FaMapMarkerAlt, FaCity, FaGlobeAsia } from 'react-icons/fa';
import './GroundwaterSearchFilter.css';

const GroundwaterSearchFilter = ({ 
  statesData, 
  currentStateData, 
  villageData,
  allDistrictsData,
  selectedState,
  selectedDistrict,
  onStateSelect,
  onDistrictSelect,
  onVillageSelect,
  onReset,
  geoData
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [allVillagesCache, setAllVillagesCache] = useState({});
  const [isLoadingVillages, setIsLoadingVillages] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Load villages dynamically when searching using the search API
  useEffect(() => {
    const searchVillages = async () => {
      if (!searchTerm || searchTerm.length < 3) {
        return;
      }
      
      setIsLoadingVillages(true);
      
      try {
        // Always search for villages when user types 3+ characters
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/villages/search?q=${encodeURIComponent(searchTerm)}`
        );
        const result = await response.json();
        
        console.log('Village search response:', result);
        
        if (result.success && result.results && result.results.length > 0) {
          // Convert search results to our cache format
          const newCache = { ...allVillagesCache };
          
          result.results.forEach(village => {
            const cacheKey = `${village.state}-${village.district}`;
            
            if (!newCache[cacheKey]) {
              newCache[cacheKey] = [];
            }
            
            // Add village if not already in cache
            const exists = newCache[cacheKey].some(v => 
              (v.villageName || v.name) === village.village
            );
            
            if (!exists) {
              newCache[cacheKey].push({
                villageName: village.village,
                name: village.village,
                village: village.village, // Add this for modal compatibility
                districtName: village.district,
                district: village.district, // Add this for modal compatibility
                stateName: village.state,
                state: village.state, // Add this for modal compatibility
                block: village.block,
                category: village.category,
                latitude: village.latitude,
                longitude: village.longitude,
                waterLevel: village.waterLevel,
                date: village.date
              });
            }
          });
          
          setAllVillagesCache(newCache);
          console.log(`Found ${result.results.length} villages matching "${searchTerm}"`);
        } else {
          console.log(`No villages found matching "${searchTerm}"`, result);
        }
      } catch (error) {
        console.error('Error searching villages:', error);
      } finally {
        setIsLoadingVillages(false);
      }
    };
    
    // Debounce the village search
    const timeoutId = setTimeout(searchVillages, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Build comprehensive search index
  const searchIndex = useMemo(() => {
    const index = [];

    // Add states
    if (statesData && statesData.length > 0) {
      statesData.forEach(state => {
        if (state && state.name) {
          index.push({
            type: 'state',
            name: state.name,
            displayName: state.name,
            category: state.category,
            data: state
          });
        }
      });
    }

    // Add ALL districts from allDistrictsData (comprehensive district list)
    if (allDistrictsData && Object.keys(allDistrictsData).length > 0) {
      Object.entries(allDistrictsData).forEach(([stateName, districts]) => {
        if (districts && Array.isArray(districts)) {
          districts.forEach(district => {
            if (district && district.name) {
              // Convert state name from uppercase back to proper case
              const properStateName = statesData?.find(s => 
                s.name.toUpperCase() === stateName
              )?.name || stateName;
              
              index.push({
                type: 'district',
                name: district.name,
                displayName: `${district.name}, ${properStateName}`,
                stateName: properStateName,
                category: district.category,
                data: district
              });
            }
          });
        }
      });
    }

    // Add villages from current selection
    if (villageData && villageData.length > 0) {
      villageData.forEach(village => {
        const villageName = village?.villageName || village?.name;
        if (villageName) {
          index.push({
            type: 'village',
            name: villageName,
            displayName: `${villageName}, ${village.districtName || selectedDistrict || 'Unknown'}, ${village.stateName || selectedState || 'Unknown'}`,
            districtName: village.districtName || selectedDistrict,
            stateName: village.stateName || selectedState,
            category: village.category,
            data: village
          });
        }
      });
    }
    
    // Add villages from cache (for search results)
    if (allVillagesCache && Object.keys(allVillagesCache).length > 0) {
      Object.entries(allVillagesCache).forEach(([cacheKey, villages]) => {
        if (villages && Array.isArray(villages)) {
          villages.forEach(village => {
            const villageName = village?.villageName || village?.name;
            if (villageName) {
              // Check if already added from villageData
              const exists = index.some(item => 
                item.type === 'village' && 
                item.name === villageName &&
                item.districtName === village.districtName
              );
              
              if (!exists) {
                index.push({
                  type: 'village',
                  name: villageName,
                  displayName: `${villageName}, ${village.districtName || 'Unknown'}, ${village.stateName || 'Unknown'}`,
                  districtName: village.districtName,
                  stateName: village.stateName,
                  category: village.category,
                  data: village
                });
              }
            }
          });
        }
      });
    }

    console.log('Search index built:', {
      total: index.length,
      states: index.filter(i => i.type === 'state').length,
      districts: index.filter(i => i.type === 'district').length,
      villages: index.filter(i => i.type === 'village').length
    });

    return index;
  }, [statesData, allDistrictsData, villageData, selectedState, selectedDistrict, allVillagesCache]);

  // Filter results based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredResults([]);
      setIsOpen(false);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const results = searchIndex.filter(item => {
      // Safety check - ensure name and displayName exist
      if (!item || !item.name || !item.displayName) return false;
      
      const itemName = item.name.toLowerCase();
      const itemDisplayName = item.displayName.toLowerCase();
      
      return itemName.includes(term) || itemDisplayName.includes(term);
    });

    // Sort by relevance (exact matches first, then starts with, then contains)
    results.sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      
      if (aName === term) return -1;
      if (bName === term) return 1;
      if (aName.startsWith(term)) return -1;
      if (bName.startsWith(term)) return 1;
      return 0;
    });

    // Limit to 50 results for performance
    setFilteredResults(results.slice(0, 50));
    setIsOpen(results.length > 0);
    setHighlightedIndex(-1);
  }, [searchTerm, searchIndex]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredResults.length) {
          handleSelect(filteredResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Helper function to get district center from GeoJSON
  const getDistrictCenter = useCallback((districtName, stateName) => {
    if (!geoData || !geoData.features) return null;
    
    // Normalize names for matching
    const normalizeStateName = (name) => {
      if (!name) return '';
      return name.toUpperCase().replace(/\s+/g, '').replace(/AND/g, 'AND');
    };
    
    const normalizedStateName = normalizeStateName(stateName);
    const normalizedDistrictName = normalizeStateName(districtName);
    
    // Find the district feature
    const districtFeature = geoData.features.find(feature => {
      const featureStateName = feature.properties?.st_nm || feature.properties?.NAME_1;
      const featureDistrictName = feature.properties?.NAME_2;
      
      if (!featureStateName || !featureDistrictName) return false;
      
      const matchesState = normalizeStateName(featureStateName) === normalizedStateName;
      const matchesDistrict = normalizeStateName(featureDistrictName) === normalizedDistrictName ||
                             normalizeStateName(featureDistrictName).includes(normalizedDistrictName) ||
                             normalizedDistrictName.includes(normalizeStateName(featureDistrictName));
      
      return matchesState && matchesDistrict;
    });
    
    if (!districtFeature) return null;
    
    // Calculate center from geometry
    const coords = districtFeature.geometry.coordinates;
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    
    const processCoords = (coordArray) => {
      coordArray.forEach(item => {
        if (Array.isArray(item[0])) {
          processCoords(item);
        } else {
          const [lng, lat] = item;
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
        }
      });
    };
    
    processCoords(coords);
    
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    return [centerLat, centerLng];
  }, [geoData]);

  // Handle selection
  const handleSelect = (item) => {
    if (!item) return;
    
    try {
      // Close dropdown and clear search immediately to prevent re-searching
      setIsOpen(false);
      setHighlightedIndex(-1);
      
      switch (item.type) {
        case 'state':
          if (item.name && onStateSelect) {
            onStateSelect(item.name);
            setSearchTerm(''); // Clear search after selection
          }
          break;
        case 'district':
          if (item.name && item.stateName) {
            if (selectedState !== item.stateName && onStateSelect) {
              onStateSelect(item.stateName);
            }
            if (onDistrictSelect) {
              // Get district center and pass it along
              const center = getDistrictCenter(item.name, item.stateName);
              onDistrictSelect(item.name, center);
            }
            setSearchTerm(''); // Clear search after selection
          }
          break;
        case 'village':
          if (item.data) {
            if (item.stateName && selectedState !== item.stateName && onStateSelect) {
              onStateSelect(item.stateName);
            }
            if (item.districtName && selectedDistrict !== item.districtName && onDistrictSelect) {
              const center = getDistrictCenter(item.districtName, item.stateName);
              onDistrictSelect(item.districtName, center);
            }
            if (onVillageSelect) {
              // Small delay to ensure state and district are set first
              setTimeout(() => {
                onVillageSelect(item.data);
              }, 100);
            }
            setSearchTerm(''); // Clear search after selection
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error handling selection:', error);
    }
  };

  // Clear search
  const handleClear = () => {
    setSearchTerm('');
    setFilteredResults([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (onReset) {
      onReset();
    }
    inputRef.current?.focus();
  };

  // Get icon for result type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'state':
        return <FaGlobeAsia className="gw-search-result-icon state" />;
      case 'district':
        return <FaCity className="gw-search-result-icon district" />;
      case 'village':
        return <FaMapMarkerAlt className="gw-search-result-icon village" />;
      default:
        return null;
    }
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'Safe': '#1fc21f',
      'Semi Critical': '#f9c802',
      'Critical': '#ff8d1a',
      'Over Exploited': '#ea2328',
      'Saline': '#2ba6ff',
      'No Data': '#e6e6e6'
    };
    return colors[category] || '#e6e6e6';
  };

  return (
    <div className="gw-search-filter" ref={searchRef}>
      <div className="gw-search-input-container">
        <FaSearch className="gw-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="gw-search-input"
          placeholder="Search states, districts, or villages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm && setIsOpen(true)}
        />
        {searchTerm && (
          <button 
            className="gw-search-clear" 
            onClick={handleClear}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {isOpen && filteredResults.length > 0 && (
        <div className="gw-search-dropdown">
          <div className="gw-search-results-header">
            <span>{filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''} found</span>
            {isLoadingVillages && (
              <span className="gw-search-loading-indicator">Loading villages...</span>
            )}
          </div>
          <ul className="gw-search-results">
            {filteredResults.map((result, index) => (
              <li
                key={`${result.type}-${result.name}-${index}`}
                className={`gw-search-result-item ${highlightedIndex === index ? 'highlighted' : ''}`}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="gw-search-result-content">
                  {getTypeIcon(result.type)}
                  <div className="gw-search-result-text">
                    <div className="gw-search-result-name">{result.displayName}</div>
                    <div className="gw-search-result-meta">
                      <span className="gw-search-result-type">{result.type}</span>
                      {result.category && (
                        <span 
                          className="gw-search-result-category"
                          style={{ 
                            backgroundColor: getCategoryColor(result.category),
                            color: result.category === 'No Data' ? '#666' : 'white'
                          }}
                        >
                          {result.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isOpen && searchTerm && filteredResults.length === 0 && (
        <div className="gw-search-dropdown">
          <div className="gw-search-no-results">
            <FaSearch className="gw-search-no-results-icon" />
            <p>No results found for "{searchTerm}"</p>
            <small>Try searching for a state, district, or village name</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroundwaterSearchFilter;
